// Raspberry Pi Pico (RP2040). Medidas de la hoja de datos de la Pico: placa de 51 × 21 mm y 1 mm de grueso,
// 40 pines almenados a 2.54 mm en dos filas separadas 17.78 mm (quedan en las filas c y h de la protoboard,
// sobre el canal). Se dibuja acostada con el micro-USB a la izquierda: el pin 1 (GP0) queda abajo a la
// izquierda, los pines 1–20 en la fila de abajo y los 21–40 en la de arriba, de derecha a izquierda.
// Los nombres de los pines son los de wokwi-pi-pico, salvo el pin 33, que aquí se llama AGND (en Wokwi, GND.7).
import { definePart, PITCH } from './registry.js';

export const PICO = {
  W: 51,
  D: 21,
  PCB_Y: 2.5, // sobre los pines de la tira (como el Nano)
  PCB_T: 1,
  ROW_Z: 17.78 / 2,
};
PICO.PCB_TOP = PICO.PCB_Y + PICO.PCB_T;

// Funciones de cada GPIO con las asignaciones por omisión de arduino-pico (variante rpipico).
const GP_LABEL = {
  0: 'UART0 TX (Serial1)', 1: 'UART0 RX (Serial1)', 2: 'I²C1 SDA · SPI0 SCK', 3: 'I²C1 SCL · SPI0 TX',
  4: 'I²C0 SDA (Wire)', 5: 'I²C0 SCL (Wire)', 6: 'I²C1 SDA', 7: 'I²C1 SCL', 8: 'UART1 TX (Serial2)', 9: 'UART1 RX (Serial2)',
  10: 'SPI1 SCK', 11: 'SPI1 TX', 12: 'SPI1 RX (MISO de SPI1)', 13: 'SPI1 CS', 14: 'SPI1 SCK (SPI1)', 15: 'SPI1 TX (MOSI de SPI1)',
  16: 'SPI0 RX (MISO de SPI)', 17: 'SPI0 CS (SPI)', 18: 'SPI0 SCK (SPI)', 19: 'SPI0 TX (MOSI de SPI)', 20: 'I²C0 SDA', 21: 'I²C0 SCL',
  22: '', 26: 'ADC0 (A0) · I²C1 SDA (Wire1)', 27: 'ADC1 (A1) · I²C1 SCL (Wire1)', 28: 'ADC2 (A2)',
};

const OTHER = {
  'GND.1': 'GND', 'GND.2': 'GND', 'GND.3': 'GND', 'GND.4': 'GND', 'GND.5': 'GND', 'GND.6': 'GND', 'GND.8': 'GND',
  AGND: 'AGND · tierra del ADC',
  RUN: 'RUN · a GND reinicia el RP2040',
  ADC_VREF: 'ADC_VREF · referencia del ADC (3.3 V filtrados)',
  '3V3': '3V3 · salida de 3.3 V (hasta 300 mA)',
  '3V3_EN': '3V3_EN · a GND apaga el regulador',
  VSYS: 'VSYS · entrada de 1.8 a 5.5 V',
  VBUS: 'VBUS · 5 V del USB',
};

// Pines físicos 1–40.
const PHYSICAL = [
  'GP0', 'GP1', 'GND.1', 'GP2', 'GP3', 'GP4', 'GP5', 'GND.2', 'GP6', 'GP7', 'GP8', 'GP9', 'GND.3', 'GP10', 'GP11', 'GP12', 'GP13',
  'GND.4', 'GP14', 'GP15', 'GP16', 'GP17', 'GND.5', 'GP18', 'GP19', 'GP20', 'GP21', 'GND.6', 'GP22', 'RUN', 'GP26', 'GP27', 'AGND',
  'GP28', 'ADC_VREF', '3V3', '3V3_EN', 'GND.8', 'VSYS', 'VBUS',
];

/** GPIO del RP2040 que salen a los pines (nombre del pin → número de GPIO). */
export const PICO_GPIO = {};
for (const name of PHYSICAL) if (name.startsWith('GP')) PICO_GPIO[name] = Number(name.slice(2));

function picoLabel(name) {
  if (name in OTHER) return OTHER[name];
  const n = PICO_GPIO[name];
  return GP_LABEL[n] ? `${name} · ${GP_LABEL[n]}` : name;
}

function picoKind(name) {
  if (name.startsWith('GND') || name === 'AGND') return 'gnd';
  if (['3V3', 'VSYS', 'VBUS', 'ADC_VREF'].includes(name)) return 'power';
  if (name === 'GP26' || name === 'GP27' || name === 'GP28') return 'analog';
  return name.startsWith('GP') ? 'digital' : 'signal';
}

const picoPins = PHYSICAL.map((name, i) => {
  // Pines 1–20 abajo (z > 0) de izquierda a derecha; 21–40 arriba de derecha a izquierda.
  const bottom = i < 20;
  const col = bottom ? i : 39 - i;
  return { name, label: picoLabel(name), kind: picoKind(name), x: (col - 9.5) * PITCH, y: 0, z: bottom ? PICO.ROW_Z : -PICO.ROW_Z, insert: true, physical: i + 1 };
});

export const PICO_TEMPLATE = `void setup() {
  // Se ejecuta una vez al encender o reiniciar la Pico.
  Serial.begin(115200);       // en la Pico, Serial es el puerto USB
  while (!Serial) delay(10);  // espera a que se abra el puerto: lo que se imprime antes se pierde
}

void loop() {
  // Se repite sin parar.
}
`;

definePart({
  type: 'pi-pico',
  name: 'Raspberry Pi Pico',
  category: 'placas',
  description: 'RP2040 a 125 MHz con 264 KB de RAM y 2 MB de flash. 26 GPIO de 3.3 V (no toleran 5 V), 3 entradas analógicas de 12 bits, Serial por USB, 2 UART, 2 I²C, 2 SPI, PWM y PIO.',
  keywords: 'rp2040 pico raspberry rpi microcontrolador 3.3v',
  help:
    'Lógica de <b>3.3 V</b>: los GPIO no toleran 5 V (avisa arriba de 3.6 V; usa un divisor o un convertidor de nivel). ' +
    'Alimentación por USB: VBUS = 5 V, VSYS = VBUS menos el diodo (unos 4.7 V; ahí también puede ir una batería de 1.8–5.5 V) ' +
    'y 3V3 = salida del regulador RT6150. LED integrado en GP25 (<code>LED_BUILTIN</code>). ' +
    '<code>Serial</code> es el USB: lo que se imprime antes de que se abra el puerto se pierde (usa <code>while (!Serial) delay(10);</code>). ' +
    '<code>Serial1</code> en GP0/GP1 y <code>Serial2</code> en GP8/GP9. ' +
    'I²C: <code>Wire</code> en GP4 (SDA) y GP5 (SCL), <code>Wire1</code> en GP26/GP27. SPI: GP16 (MISO), GP17 (CS), GP18 (SCK), GP19 (MOSI). ' +
    '<code>analogRead</code> en A0–A2 (GP26–GP28, 10 bits por omisión; <code>analogReadResolution(12)</code> da 12); A3 mide VSYS/3. ' +
    'Todos los GPIO tienen PWM. El botón BOOTSEL se lee en el sketch con <code>BOOTSEL</code>; EEPROM.commit() sí se guarda en la flash. ' +
    'Se compila con arduino-pico a 125 MHz. No se simulan: el segundo núcleo (<code>setup1()</code>/<code>loop1()</code>), ' +
    'el modo de carga por USB de BOOTSEL, LittleFS (la placa va sin sistema de archivos) ni el WiFi/Bluetooth (eso es la Pico W).',
  kind: 'board',
  insertable: true,
  pins: picoPins,
  buses: [['GND.1', 'GND.2', 'GND.3', 'GND.4', 'GND.5', 'GND.6', 'GND.8', 'AGND']],
  footprint: { w: PICO.W + 1.4, d: PICO.D, h: 7, x: -0.7 },
  template: PICO_TEMPLATE,
  mcu: {
    arch: 'rp2040',
    chip: 'rp2040',
    board: () => 'pico', // id de compilación (server/compile.js; ver parts/board-id.js)
    fqbn: 'rp2040:rp2040:rpipico:freq=125',
    gpio: PICO_GPIO,
    builtinLed: 25,
  },
});
